import { Button, MenuItem, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { useToast } from '../../../hooks/useToast';
import type { AdminMetric } from './types';
import { parseCommaSeparated } from './utils';

export function EditSystemMetricDrawer({
  open,
  onClose,
  orgId,
  metric,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  metric: AdminMetric;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [keywords, setKeywords] = useState(metric.config.keywords.join(', '));
  const [categoryId, setCategoryId] = useState<string | null>(
    metric.categoryId ?? null,
  );

  const categoriesQuery = trpc.admin.metrics.getCategoriesByOrg.useQuery({
    orgId,
  });
  const categories = categoriesQuery.data ?? [];

  const updateKeywordsMutation =
    trpc.admin.metrics.updateMetricKeywords.useMutation();
  const updateMetricMutation = trpc.admin.metrics.updateMetric.useMutation();

  const keywordsChanged = keywords !== metric.config.keywords.join(', ');
  const categoryChanged = categoryId !== (metric.categoryId ?? null);
  const isSaving =
    updateKeywordsMutation.isLoading || updateMetricMutation.isLoading;

  // Keywords and category live on different mutations — updateMetricKeywords
  // takes a row lock and only touches config.keywords, so a category-only
  // updateMetric payload can't clobber it.
  const handleSave = async () => {
    let categorySaved = false;
    try {
      if (categoryChanged) {
        await updateMetricMutation.mutateAsync({
          orgId,
          id: metric.id,
          categoryId,
        });
        categorySaved = true;
      }
      if (keywordsChanged) {
        await updateKeywordsMutation.mutateAsync({
          orgId,
          id: metric.id,
          keywords: parseCommaSeparated(keywords),
        });
      }
      toast({ title: 'Metric updated', severity: 'success' });
      onSuccess();
    } catch {
      if (categorySaved) {
        // The category write already landed — refresh so the table isn't
        // stale, and say exactly which half failed.
        onSuccess();
        toast({
          title: 'Category saved, but keywords failed to update',
          severity: 'error',
        });
      } else {
        toast({ title: 'Error updating metric', severity: 'error' });
      }
    }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">Edit Metric</Typography>
      </DrawerHeader>

      <div className="flex flex-col grow px-6 gap-4 overflow-y-auto">
        <TextField
          select
          label="Category"
          value={categoryId ?? ''}
          onChange={(e) =>
            setCategoryId(e.target.value === '' ? null : e.target.value)
          }
          fullWidth
        >
          <MenuItem value="">None</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Keywords"
          placeholder="AMRAP, EMOM"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          fullWidth
        />
      </div>

      <DrawerFooter className="flex flex-row gap-2 w-full p-6 justify-between">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || (!keywordsChanged && !categoryChanged)}
        >
          Save
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
